import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Target, Mail, Map as MapIcon, PhoneCall } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_META } from "@/lib/status";

const CRITICAL = ["LOCKDOWN", "VISITATION_SUSPENDED", "EMERGENCY"];
const CAUTION = ["LIMITED_VISITATION", "RESTRICTED"];

export default function TravelerAlertPage() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiClient
      .get("/facilities")
      .then((r) => setFacilities(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const critical = facilities.filter((f) => CRITICAL.includes(f.status));
  const caution = facilities.filter((f) => CAUTION.includes(f.status));
  const normal = facilities.filter((f) => f.status === "NORMAL");

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="traveler-alert-page">
      {/* Hero warning */}
      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-red-700 shrink-0" />
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-red-900">Before you travel</h1>
            <p className="mt-1 text-sm text-red-900 leading-relaxed max-w-3xl">
              Correctional facility conditions can change without notice.{" "}
              <span className="font-semibold">
                Confirm visitation directly with the institution before making a long trip.
              </span>{" "}
              This page shows every facility we're tracking with an active restriction so you can plan around it.
            </p>
          </div>
        </div>
      </div>

      {/* Three quick actions */}
      <section className="mt-6 grid gap-4 md:grid-cols-3">
        <Card className="p-5 border-slate-200">
          <Mail className="text-blue-600" size={20} />
          <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">Free email alerts</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Open any facility and tap <span className="font-semibold">Get status alerts</span>. We email you the moment the facility's status changes.
          </p>
          <Link to="/facilities" className="mt-3 inline-block text-sm font-semibold text-blue-700 hover:underline" data-testid="alert-browse-facilities">
            Browse facilities →
          </Link>
        </Card>
        <Card className="p-5 border-2 border-amber-200 bg-amber-50">
          <Target className="text-amber-600" size={20} />
          <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">Track a specific inmate · $2/mo</h3>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            Get alerts when an inmate's facility status changes <em>and</em> when they're transferred to a new institution.
          </p>
          <Link to="/tracker" className="mt-3 inline-block text-sm font-semibold text-amber-900 hover:underline" data-testid="alert-tracker-link">
            Start tracking →
          </Link>
        </Card>
        <Card className="p-5 border-slate-200">
          <MapIcon className="text-emerald-600" size={20} />
          <h3 className="mt-3 font-heading font-bold text-lg text-slate-900">Status map</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            See every tracked facility nationwide, color-coded by live operational status.
          </p>
          <Link to="/map" className="mt-3 inline-block text-sm font-semibold text-emerald-700 hover:underline" data-testid="alert-map-link">
            Open map →
          </Link>
        </Card>
      </section>

      {/* Critical */}
      <section className="mt-10">
        <h2 className="font-heading text-lg font-bold text-slate-900">
          🔴 Active restrictions &amp; alerts
          {!loading && <span className="font-mono text-xs text-slate-500 ml-2">({critical.length})</span>}
        </h2>
        <p className="mt-1 text-xs text-slate-500">Facilities on lockdown, with visitation suspended, or responding to an incident.</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {critical.map((f) => <AlertRow key={f.id} f={f} />)}
          {!loading && critical.length === 0 && (
            <Card className="p-4 border-slate-200 text-sm text-slate-600 md:col-span-2 bg-emerald-50 border-emerald-200">
              Good news — no facilities currently have a lockdown or suspended visitation flagged.
            </Card>
          )}
        </div>
      </section>

      {caution.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-lg font-bold text-slate-900">
            🟡 Caution — restricted or limited visitation
            <span className="font-mono text-xs text-slate-500 ml-2">({caution.length})</span>
          </h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {caution.map((f) => <AlertRow key={f.id} f={f} />)}
          </div>
        </section>
      )}

      {normal.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-lg font-bold text-slate-900">
            🟢 Normal operations
            <span className="font-mono text-xs text-slate-500 ml-2">({normal.length})</span>
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Verified normal within the last status check. Still confirm visitation directly before travel.
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {normal.slice(0, 10).map((f) => <AlertRow key={f.id} f={f} />)}
          </div>
          {normal.length > 10 && (
            <Link to="/facilities" className="mt-3 inline-block text-sm text-blue-700 hover:underline" data-testid="alert-see-all">
              View all {normal.length} normal-operations facilities →
            </Link>
          )}
        </section>
      )}

      {/* Emergency help */}
      <Card className="mt-10 p-5 border-slate-200 bg-slate-50">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-slate-900 text-white">
            <PhoneCall size={16} />
          </div>
          <div>
            <h3 className="font-heading font-bold text-slate-900">Emergency involving an incarcerated person</h3>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              Open the facility's profile and call the official phone number listed there. Never travel without first confirming the current situation with the facility directly.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function AlertRow({ f }) {
  const label = (STATUS_META[f.status]?.label || "").toLowerCase();
  const note = (f.visitation_status || "").trim().toLowerCase();
  const showNote = note && note !== label && !label.startsWith(note);
  return (
    <Card className="relative p-4 border-slate-200 hover:border-slate-900 hover:shadow-sm transition-colors" data-testid={`alert-row-${f.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.system}</div>
          <Link to={`/facility/${f.id}`} className="mt-0.5 block font-heading font-bold text-slate-900 truncate after:absolute after:inset-0">
            {f.name}
          </Link>
          <div className="mt-1 text-xs text-slate-600">{[f.city, f.state].filter(Boolean).join(", ")}</div>
          {showNote && <div className="mt-1.5 text-xs text-slate-700">{f.visitation_status}</div>}
        </div>
        <StatusBadge status={f.status} size="sm" hideUnknown />
      </div>
    </Card>
  );
}
